import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Download, ChevronRight, Layers } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { listBatchCases, listBatches } from "@/lib/api";
import ScoreBadge from "@/components/ScoreBadge";
import StatusBadge from "@/components/StatusBadge";
import { SkeletonBlock } from "@/components/Skeletons";
import { downloadBatchZip } from "@/lib/exports";

export default function BatchView() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [batches, setBatches] = useState([]);
  const [err, setErr] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let active = true;
    setErr(null);
    listBatches().then((bs) => active && setBatches(bs));
    if (batchId) {
      setItems(null);
      listBatchCases(batchId)
        .then((rows) => active && setItems(rows))
        .catch((e) => active && setErr(e.message || "Failed to load batch"));
    } else {
      setItems([]);
    }
    return () => {
      active = false;
    };
  }, [batchId]);

  const handleZip = async () => {
    if (!items || items.length === 0) return;
    setExporting(true);
    try {
      await downloadBatchZip(items, batchId);
      toast.success("Export complete", { description: `${items.length} PDFs zipped.` });
    } catch (e) {
      toast.error("Export failed", { description: e.message });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="batch-view-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Batch</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            {batchId ? <span className="mono">{batchId}</span> : "Batches"}
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">
            All images in this batch with their status and overall score. Open any row to view
            the report.
          </p>
        </div>
        {batchId && items && items.length > 0 && (
          <button
            onClick={handleZip}
            disabled={exporting}
            data-testid="export-zip-button"
            className="inline-flex items-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 disabled:opacity-60 transition-colors"
          >
            <Download className="h-4 w-4" /> {exporting ? "Zipping…" : "Export all (ZIP)"}
          </button>
        )}
      </header>

      {/* Batch picker when no batchId */}
      {!batchId && (
        <section
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="batch-list"
        >
          {batches.length === 0 ? (
            <div className="md:col-span-2 rounded-xl border border-[#272A30] bg-[#111318] p-10 text-center">
              <Layers className="h-8 w-8 mx-auto text-zinc-500" />
              <div className="mt-3 text-zinc-300 font-medium">No batches yet</div>
              <p className="text-sm text-zinc-500 mt-1">
                Start a batch from <Link to="/analyze" className="text-zinc-200 underline">New analysis</Link>.
              </p>
            </div>
          ) : (
            batches.map((b) => (
              <button
                key={b.id}
                onClick={() => navigate(`/batch/${b.id}`)}
                data-testid={`batch-card-${b.id}`}
                className="text-left rounded-xl border border-[#272A30] bg-[#111318] p-5 hover:border-[#4A4E58] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{b.name}</div>
                  <ChevronRight className="h-4 w-4 text-zinc-500" />
                </div>
                <div className="mt-2 text-[11px] text-zinc-500 mono">{b.id}</div>
                <div className="mt-4 flex items-center gap-3 text-xs text-zinc-400">
                  <span>
                    <span className="text-zinc-200 font-medium">{b.complete}</span>/
                    {b.total} complete
                  </span>
                  <span className="text-zinc-700">•</span>
                  <span className="text-red-400">{b.flagged} flagged</span>
                  <span className="text-zinc-700">•</span>
                  <span>{new Date(b.submittedAt).toLocaleString()}</span>
                </div>
              </button>
            ))
          )}
        </section>
      )}

      {err && (
        <div
          role="alert"
          data-testid="batch-error"
          className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3"
        >
          {err}
        </div>
      )}

      {batchId && (
        <section className="rounded-xl border border-[#272A30] bg-[#111318] overflow-hidden">
          {!items ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonBlock key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="batch-empty">
              <div className="text-zinc-300 font-medium">No items in this batch</div>
              <p className="text-sm text-zinc-500 mt-1">
                It may have been cleared or expired.
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-5 py-3 text-left font-medium">Image</th>
                  <th className="px-5 py-3 text-left font-medium">File / URL</th>
                  <th className="px-5 py-3 text-left font-medium">Status</th>
                  <th className="px-5 py-3 text-left font-medium">Score</th>
                  <th className="px-2 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/report/${c.id}`)}
                    data-testid={`batch-row-${c.id}`}
                    className="border-t border-[#272A30]/60 hover:bg-[#14171D] cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3">
                      <img
                        src={c.thumbnail}
                        alt=""
                        className="h-10 w-14 rounded-md object-cover ring-1 ring-[#272A30]"
                      />
                    </td>
                    <td className="px-5 py-3 text-sm text-zinc-200">
                      <div className="font-medium truncate max-w-xs">{c.fileName}</div>
                      <div className="text-[11px] text-zinc-500 mono truncate max-w-xs">
                        {c.submittedUrl || c.sourceDomain || c.id}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={c.flagged ? "flagged" : c.status} />
                    </td>
                    <td className="px-5 py-3">
                      <ScoreBadge score={c.score} />
                    </td>
                    <td className="px-2 py-3 text-zinc-500">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
